// src/components/support/supportUi.js
// Look-and-feel shared by the three support pages (form, chat, ticket), so
// they read as one flow instead of three separate screens.
import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, LogIn } from 'lucide-react';
import AppBackground from '../AppBackground';
import { useAuth } from '../../context/AuthContext';

export const ACCENT = '#f59e0b';
export const LINK = '#f97316';
export const INK = '#1e1b4b';
export const INK_DIM = '#57527e';
export const INK_FAINT = '#6b6892';

export const card = {
  borderRadius: 18,
  background: 'linear-gradient(160deg, rgba(255,255,255,0.7), rgba(255,255,255,0.35))',
  backdropFilter: 'blur(10px)', WebkitBackdropFilter: 'blur(10px)',
  border: '1px solid rgba(30,27,75,0.08)',
};

export const fieldBase = {
  width: '100%', boxSizing: 'border-box',
  padding: '12px 14px', fontSize: 15, color: INK,
  background: 'rgba(255,255,255,0.75)',
  border: '1px solid rgba(30,27,75,0.16)', borderRadius: 12,
  outline: 'none', fontFamily: 'inherit',
};

export const primaryButton = {
  cursor: 'pointer', padding: '12px 22px', borderRadius: 12, fontSize: 15, fontWeight: 600,
  background: ACCENT, color: INK, border: '1px solid transparent',
};

export const outlineButton = {
  cursor: 'pointer', padding: '12px 22px', borderRadius: 12, fontSize: 15, fontWeight: 600,
  background: 'transparent', color: INK, border: '1px solid rgba(30,27,75,0.35)',
};

export function Field({ label, htmlFor, required, children, hint }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label htmlFor={htmlFor} style={{ display: 'block', fontSize: 13, fontWeight: 600, color: INK, marginBottom: 6 }}>
        {label}{required && <span style={{ color: '#dc2626' }} aria-hidden="true"> *</span>}
      </label>
      {children}
      {hint && <div style={{ fontSize: 12.5, color: INK_FAINT, marginTop: 5 }}>{hint}</div>}
    </div>
  );
}

// Page frame: Aurora background, sticky back link, centred column.
export function SupportShell({ children, backTo = '/', backLabel = 'Back to Elpis', maxWidth = 640 }) {
  return (
    <div style={{ minHeight: '100vh', background: '#fffdfa', color: INK, position: 'relative' }}>
      <AppBackground />
      <nav style={{
        position: 'sticky', top: 0, zIndex: 10, display: 'flex', alignItems: 'center', gap: 16,
        padding: '20px max(16px, 4vw)',
        background: 'rgba(255,253,250,0.85)', backdropFilter: 'blur(20px)',
        borderBottom: '1px solid rgba(30,27,75,0.06)',
      }}>
        <Link to={backTo} style={{
          display: 'inline-flex', alignItems: 'center', gap: 6, color: INK_DIM, fontSize: 14, textDecoration: 'none',
        }}>
          <ArrowLeft size={16} /> {backLabel}
        </Link>
      </nav>
      <main style={{ maxWidth, margin: '0 auto', padding: '40px 16px 80px', position: 'relative', zIndex: 1 }}>
        {children}
      </main>
      <style>{`
        .support-name-row { display: grid; grid-template-columns: 1fr 1fr; gap: 14px; }
        @media (max-width: 520px) { .support-name-row { grid-template-columns: 1fr; gap: 0; } }
        main input:focus, main select:focus, main textarea:focus {
          border-color: rgba(245,158,11,0.6) !important; box-shadow: 0 0 0 3px rgba(245,158,11,0.12);
        }
      `}</style>
    </div>
  );
}

// Support needs a signed-in student so every ticket is tracked against a
// customer number. Signed-out visitors get this card instead of the page; the
// only thing they can still send is the "Can't sign in?" form on /login.
export function RequireSupportSignIn({ children }) {
  const { currentUser, loading } = useAuth();
  if (loading) return null;
  if (currentUser) return children;
  return (
    <SupportShell backTo="/">
      <section style={{ ...card, padding: '36px 24px', textAlign: 'center' }}>
        <LogIn size={34} color={ACCENT} aria-hidden="true" />
        <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: -0.6, margin: '12px 0 8px' }}>Sign in to contact support</h1>
        <p style={{ fontSize: 15, color: INK_DIM, lineHeight: 1.6, margin: '0 0 22px' }}>
          We link every request to your Elpis account so we can find your details fast and keep track of
          your ticket. Sign in and you'll be right back here.
        </p>
        <Link to="/login" style={{ ...primaryButton, textDecoration: 'none', display: 'inline-block' }}>Sign in</Link>
        <p style={{ fontSize: 13, color: INK_FAINT, margin: '18px 0 0', lineHeight: 1.6 }}>
          Can't sign in at all? Use the "Can't sign in?" link on the sign-in page and we'll reply by email.
        </p>
      </section>
    </SupportShell>
  );
}
